import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowRightIcon, 
  StarIcon, 
  TruckIcon, 
  ShieldCheckIcon, 
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  UserIcon,
  ShoppingBagIcon as ShoppingBagOutlineIcon,
  ShoppingCartIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon, HeartIcon as HeartOutlineIcon } from '@heroicons/react/24/solid';
import { ShoppingBagIcon as ShoppingBagSolidIcon } from '@heroicons/react/24/solid';
import zaraImg from '../../assets/zara.jpeg';
import hmImg from '../../assets/hm.jpeg';
import nikeImg from '../../assets/nike.jpeg';
import pumaImg from '../../assets/puma.jpeg';
import reebokImg from '../../assets/reebok.jpeg';
import productVideo from '../../assets/product.mp4';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import modelImage from '../../assets/models.jpeg';
import modelImage1 from '../../assets/models1.jpeg';
import modelImage2 from '../../assets/models2.jpeg';
import modelImage3 from '../../assets/models3.jpeg';
import modelImage4 from '../../assets/models4.jpeg';
import modelImage5 from '../../assets/models5.jpeg';
import { getApiUrl } from '../../config/api';
import ProductPrice from '../../components/ProductPrice';

const GOOGLE_REVIEWS_URL = 'https://www.google.com/search?q=thevastra.store+reviews';

const GoogleMark = ({ className = 'w-6 h-6' }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09A6.97 6.97 0 0 1 5.48 12c0-.73.13-1.43.36-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const Home = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [bannerLoading, setBannerLoading] = useState(true);
  const [bannerError, setBannerError] = useState(null);
  const [current, setCurrent] = useState(0);
  
  const { addToCart, isInCart, removeFromCart } = useCart();
  const { wishlist, isWishlisted, toggleWishlist } = useWishlist();

  // Color name to hex conversion function
  const getColorHex = (colorName) => {
    const colorMap = {
      'white': '#ffffff',
      'black': '#000000',
      'red': '#dc2626',
      'blue': '#2563eb',
      'green': '#16a34a',
      'yellow': '#eab308',
      'orange': '#ea580c',
      'purple': '#9333ea',
      'pink': '#ec4899',
      'gold': '#f59e0b',
      'silver': '#94a3b8'
    };
    return colorMap[colorName] || colorName;
  };

  const nextSlide = () => setCurrent((prev) => (prev + 1) % banners.length);
  const prevSlide = () => setCurrent((prev) => (prev - 1 + banners.length) % banners.length);
  const goToSlide = (idx) => setCurrent(idx);

  // Dynamic state for products, events, blogs, and categories
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState(null);

  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);
  const [blogsError, setBlogsError] = useState(null);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewsError, setReviewsError] = useState(null);

  // Carousel state for reviews
  const [reviewIndex, setReviewIndex] = useState(0);
  const reviewScrollRef = useRef(null);

  // Auto-scroll reviews - Only if more than 5 reviews
  useEffect(() => {
    if (reviews.length <= 5) return;
    const interval = setInterval(() => {
      setReviewIndex(prev => (prev + 1) % reviews.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [reviews]);

  useEffect(() => {
    if (reviewScrollRef.current) {
      reviewScrollRef.current.scrollTo({
        left: reviewIndex * 340, // 340px per card (adjust if needed)
        behavior: 'smooth',
      });
    }
  }, [reviewIndex]);

  // Top Brands
  const brands = [
    { name: 'Zara', image: zaraImg },
    { name: 'H&M', image: hmImg },
    { name: 'Nike', image: nikeImg },
    { name: 'Puma', image: pumaImg },
    { name: 'Reebok', image: reebokImg }
  ];

  // Demo fallback images
  const DEMO_PRODUCT_IMAGE = 'https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=400&q=80';
  const DEMO_EVENT_IMAGE = 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=800&q=80';
  const DEMO_BLOG_IMAGE = 'https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=600&q=80';
  const DEMO_BANNER_IMAGE = 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1200&q=80';
  const DEMO_CATEGORY_IMAGE = 'https://images.unsplash.com/photo-1558769132-cb1e7a02c813?auto=format&fit=crop&w=400&q=80';
  const DEMO_RENTAL_IMAGE = 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=400&q=80';

  const token = localStorage.getItem('token'); // Or get from your auth context

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Fetch banners
    setBannerLoading(true);
    fetch(getApiUrl('/api/admin/banners'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => {
        const mappedSlides = Array.isArray(data) ? data.flatMap(banner => {
          if (banner.isActive && banner.media && banner.media.length > 0) {
            return banner.media.map(mediaItem => ({
              type: mediaItem.type || 'image',
              src: mediaItem.url || DEMO_BANNER_IMAGE,
              alt: banner.title,
              caption: banner.title,
              subcaption: banner.description,
              titleColor: banner.titleColor || 'white',
              descriptionColor: banner.descriptionColor || 'white',
              badgeColor: banner.badgeColor || 'red',
              showBadge: banner.showBadge !== false, // Default to true
              cta: { text: 'Shop Now', link: banner.link || '/products' }
            }));
          } else {
            return []; // Return empty array if banner is inactive or has no media
          }
        }) : [];
        setBanners(mappedSlides);
        setBannerLoading(false);
      })
      .catch(err => { console.error('Error fetching banners:', err); setBannerError('Failed to load banners'); setBannerLoading(false); });

    // Fetch categories
    setCategoriesLoading(true);
    fetch(getApiUrl('/api/admin/categories-with-info'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => { 
        console.log(Array.isArray(data) ? data : []); 
        setCategories(Array.isArray(data) ? data : []); 
        setCategoriesLoading(false); 
      })
      .catch(error => {
        console.error('Error fetching categories:', error);
        setCategoriesError('Failed to load categories');
        setCategoriesLoading(false);
      });

    // Fetch products
    setProductsLoading(true);
    fetch(getApiUrl('/api/admin/products'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => {
        let products = Array.isArray(data.products) ? data.products : Array.isArray(data) ? data : [];
        // Optionally filter for featured, or just take first 4
        setFeaturedProducts(products.slice(0, 4));
        setProductsLoading(false);
      })
      .catch(err => { setProductsError('Failed to load products'); setProductsLoading(false); });
    // Fetch events
    setEventsLoading(true);
    fetch(getApiUrl('/api/admin/events'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => { setEvents(Array.isArray(data) ? data : []); setEventsLoading(false); })
      .catch(err => { setEventsError('Failed to load events'); setEventsLoading(false); });
    // Fetch blogs
    setBlogsLoading(true);
    fetch(getApiUrl('/api/admin/blogs'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => { setBlogs(Array.isArray(data) ? data : []); setBlogsLoading(false); })
      .catch(err => { setBlogsError('Failed to load blogs'); setBlogsLoading(false); });

    // Fetch Google reviews for the homepage (fallback to store reviews)
    setReviewsLoading(true);
    Promise.all([
      fetch(getApiUrl('/api/admin/google-reviews')).then(r => r.json()).catch(() => []),
      fetch(getApiUrl('/api/products/reviews/recent?all=true')).then(r => r.json()).catch(() => [])
    ])
      .then(([google, store]) => {
        const googleList = (Array.isArray(google) ? google : []).map(item => ({
          rating: item.rating,
          comment: item.comment,
          images: [],
          createdAt: item.reviewDate || item.createdAt,
          user: { name: item.authorName },
          profilePhoto: item.profilePhoto,
          source: 'google',
          reviewId: item._id
        }));
        const storeList = (Array.isArray(store) ? store : []).map(item => ({
          ...item,
          source: 'store'
        }));
        setReviews(googleList.length ? googleList : storeList);
        setReviewsLoading(false);
      })
      .catch(err => {
        console.error('Error fetching reviews:', err);
        setReviewsError('Failed to load reviews');
        setReviewsLoading(false);
      });
  }, []);

  // Remove navigation arrow buttons from the carousel section
  // Add autoplay effect for the carousel
  useEffect(() => {
    if (!banners.length) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % banners.length);
    }, 5000); // Change slide every 5 seconds
    return () => clearInterval(interval);
  }, [banners, setCurrent]);

  const handleAddToCart = (product) => {
    // You can customize size/color selection as needed
    addToCart(product, 1, 'M', 'Default');
  };

  // Dynamic tag-based product containers
  const getAllUniqueTags = () => {
    const allTags = new Set();
    featuredProducts.forEach(product => {
      if (Array.isArray(product.tags)) {
        product.tags.forEach(tag => {
          if (tag && tag.trim()) {
            allTags.add(tag.toLowerCase().trim());
          }
        });
      }
    });
    return Array.from(allTags);
  };

  const getProductsByTag = (tagName) => {
    return featuredProducts.filter(
      p => Array.isArray(p.tags) && p.tags.some(tag => tag.toLowerCase().trim() === tagName.toLowerCase().trim())
    );
  };

  // Get all unique tags
  const uniqueTags = getAllUniqueTags();

  // Legacy sections (keeping for backward compatibility)
  const galleryProducts = featuredProducts.filter(
    p => Array.isArray(p.tags) && p.tags.some(tag => tag.toLowerCase() === 'gallery')
  );
  const womenCollectionProducts = featuredProducts.filter(
    p => Array.isArray(p.tags) && p.tags.some(tag => tag.toLowerCase() === 'women-collection')
  );
  // Latest Arrival should exclude rental products
  const latestArrivalProducts = featuredProducts.filter(p => !p.isRental);

  // Pagination state for Women Collections and New Arrival
  const [womenPage, setWomenPage] = useState(1);
  const ITEMS_PER_PAGE = 8; // Show 8 items per page (2 rows of 4 on mobile, 1 row of 8 on desktop)

  // Paginated lists
  const paginatedWomen = womenCollectionProducts.slice((womenPage - 1) * ITEMS_PER_PAGE, womenPage * ITEMS_PER_PAGE);
  const womenTotalPages = Math.ceil(womenCollectionProducts.length / ITEMS_PER_PAGE);

  // Pagination state for Latest Arrival
  const [arrivalPage, setArrivalPage] = useState(1);
  const ARRIVAL_PER_PAGE = 8; // Show 8 items per page (2 rows of 4 on mobile, 1 row of 8 on desktop)
  const arrivalTotalPages = Math.ceil(latestArrivalProducts.length / ARRIVAL_PER_PAGE);
  const paginatedArrival = latestArrivalProducts.slice((arrivalPage - 1) * ARRIVAL_PER_PAGE, arrivalPage * ARRIVAL_PER_PAGE);

  // Add at the top of the Home component, after categories state:
  const [categoryPage, setCategoryPage] = useState(1);
  const CATEGORIES_PER_PAGE = 4;
  const categoryTotalPages = Math.ceil(categories.length / CATEGORIES_PER_PAGE);
  const paginatedCategories = categories.slice(
    (categoryPage - 1) * CATEGORIES_PER_PAGE,
    categoryPage * CATEGORIES_PER_PAGE
  );

  return (
    <div className="space-y-12">
      {/* Show loading spinner if any API is still loading */}
      {(bannerLoading || categoriesLoading || productsLoading || eventsLoading || blogsLoading || reviewsLoading) && (
        <div className="fixed inset-0 bg-white/80 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-600"></div>
        </div>
      )}

      {/* Carousel */}
      <section className="relative w-screen h-[80vh] md:h-[90vh] overflow-hidden">
        {bannerError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <p className="text-gray-600 mb-2">Failed to load banners</p>
              <button 
                onClick={() => window.location.reload()} 
                className="bg-pink-600 text-white px-4 py-2 rounded-lg"
              >
                Retry
              </button>
            </div>
          </div>
        )}
        {banners.length > 0 && (
          <>
            {banners[current].type === 'image' ? (
              <img
                src={banners[current].src}
                alt={banners[current].alt}
                className="absolute inset-0 w-full h-full object-cover object-center transition-all duration-700"
              />
            ) : (
              <video
                src={banners[current].src}
                className="absolute inset-0 w-full h-full object-cover object-center transition-all duration-700"
                autoPlay
                loop
                muted
                playsInline
              />
            )}
            {/* Badge - Top Right (Conditional) - Moved outside overlay */}
            {banners[current].showBadge && (
              <div className="banner-badge-ribbon-container">
                <div 
                  className="banner-badge-ribbon animate-slideInUp"
                  style={{ 
                    background: `linear-gradient(135deg, ${getColorHex(banners[current].badgeColor || 'red')}, ${getColorHex(banners[current].badgeColor || 'red')}dd)`
                  }}
                >
                  <div>LIMITED</div>
                  <div>TIME</div>
                  <div>OFFER</div>
                </div>
              </div>
            )}
            
            {/* Enhanced Banner Overlay - Bottom Positioned */}
            <div className="absolute inset-0 banner-overlay flex items-end justify-center z-10 pb-20">
              {/* Decorative Elements */}
              <div className="banner-decoration"></div>
              <div className="banner-decoration"></div>
              <div className="banner-decoration"></div>
              
              {/* Main Content */}
              <div className="banner-content-wrapper text-center banner-load">
                {/* Title */}
                <h1 
                  className="text-3xl md:text-5xl lg:text-6xl font-black mb-4 animate-slideInLeft"
                  style={{ 
                    color: getColorHex(banners[current].titleColor || 'white'),
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 255, 255, 0.9)',
                    filter: 'contrast(1.3) brightness(1.2)',
                    WebkitTextStroke: '1px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  {banners[current].caption}
                </h1>
                
                {/* Description - No white box */}
                <p 
                  className="text-lg md:text-xl lg:text-2xl font-bold mb-6 animate-slideInRight"
                  style={{ 
                    color: getColorHex(banners[current].descriptionColor || 'white'),
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 0 15px rgba(255, 255, 255, 0.7)',
                    filter: 'contrast(1.2) brightness(1.1)',
                    WebkitTextStroke: '0.5px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  {banners[current].subcaption}
                </p>
                
                {/* CTA Button */}
                <div className="animate-fadeInScale">
                  <Link
                    to="/products"
                    className="banner-cta inline-flex items-center gap-3 text-white px-6 py-3 rounded-full font-bold text-base md:text-lg transition-all duration-300"
                  >
                    <span>Shop Now</span>
                    <svg className="w-4 h-4 banner-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
            {/* Indicators - Only show when multiple banners */}
            {banners.length > 1 && (
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                {banners.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => goToSlide(idx)}
                    className={`w-1 h-1 md:w-4 md:h-4 rounded-full ${idx === current ? 'bg-pink-500' : 'bg-white/70'} border border-pink-300`}
                  />
                ))}
              </div>
            )}
          </>
        )}
        {!bannerError && banners.length === 0 && !bannerLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
          </div>
        )}
      </section>

      {/* Features */}
      <div className="w-full px-2 sm:px-4 md:px-8 py-2 sm:py-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 md:gap-6">
          <div className="feature-card flex flex-col items-center justify-center bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-lg p-3 sm:p-4 md:p-6 transition-all duration-300 hover:scale-105">
            <div className="icon-container bg-pink-100 p-2 sm:p-3 rounded-full mb-2 sm:mb-3 flex items-center justify-center">
              <TruckIcon className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-pink-600" />
            </div>
            <h3 className="font-semibold text-xs sm:text-sm md:text-lg text-pink-700 mb-1 text-center">Free Shipping</h3>
            <p className="text-xs sm:text-sm text-gray-500 text-center leading-tight">On orders above ₹1999</p>
          </div>
          <div className="feature-card flex flex-col items-center justify-center bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-lg p-3 sm:p-4 md:p-6 transition-all duration-300 hover:scale-105">
            <div className="icon-container bg-pink-100 p-2 sm:p-3 rounded-full mb-2 sm:mb-3 flex items-center justify-center">
              <ShieldCheckIcon className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-pink-600" />
            </div>
            <h3 className="font-semibold text-xs sm:text-sm md:text-lg text-pink-700 mb-1 text-center">Secure Payment</h3>
            <p className="text-xs sm:text-sm text-gray-500 text-center leading-tight">100% secure payment</p>
          </div>
          <div className="feature-card flex flex-col items-center justify-center bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-lg p-3 sm:p-4 md:p-6 transition-all duration-300 hover:scale-105">
            <div className="icon-container bg-pink-100 p-2 sm:p-3 rounded-full mb-2 sm:mb-3 flex items-center justify-center">
              <ArrowPathIcon className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-pink-600" />
            </div>
            <h3 className="font-semibold text-xs sm:text-sm md:text-lg text-pink-700 mb-1 text-center">Easy Exchange</h3>
            <p className="text-xs sm:text-sm text-gray-500 text-center leading-tight">No return policy</p>
          </div>
          <div className="feature-card flex flex-col items-center justify-center bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-lg p-3 sm:p-4 md:p-6 transition-all duration-300 hover:scale-105">
            <div className="icon-container bg-pink-100 p-2 sm:p-3 rounded-full mb-2 sm:mb-3 flex items-center justify-center">
              <StarIcon className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-pink-600" />
            </div>
            <h3 className="font-semibold text-xs sm:text-sm md:text-lg text-pink-700 mb-1 text-center">Quality Products</h3>
            <p className="text-xs sm:text-sm text-gray-500 text-center leading-tight">Authentic products</p>
          </div>
        </div>
      </div>

      {/* Shop by Category */}
      {categoriesLoading ? (
        <section className="w-full bg-gray-50 py-4 sm:py-6 md:py-8 px-2 sm:px-4 md:px-8">
          <div className="w-full px-0">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-center mb-4 sm:mb-6 md:mb-8 text-pink-900">Shop by Category</h2>
            <div className="flex items-center justify-center py-6 sm:py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
            </div>
          </div>
        </section>
      ) : categoriesError ? (
        <section className="w-full bg-gray-50 py-4 sm:py-6 md:py-8 px-2 sm:px-4 md:px-8">
          <div className="w-full px-0">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-center mb-4 sm:mb-6 md:mb-8 text-pink-900">Shop by Category</h2>
            <div className="text-center py-6 sm:py-8">
              <p className="text-gray-600 mb-4">{categoriesError}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="bg-pink-600 text-white px-4 py-2 rounded-lg"
              >
                Retry
              </button>
            </div>
          </div>
        </section>
      ) : categories.length > 0 ? (
        <section className="w-full bg-gray-50 py-4 sm:py-6 md:py-8 px-2 sm:px-4 md:px-8">
          <div className="w-full px-0">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-center mb-4 sm:mb-6 md:mb-8 text-pink-900">Shop by Category</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 h-64 sm:h-72 md:h-80">
              {paginatedCategories.map(category => {
                // Fix image path for Rental category or any category with a local image
                let imageUrl = category.image || DEMO_CATEGORY_IMAGE;
                
                // If category doesn't have an image, try to find one from products
                if (!category.image) {
                  // Find the first product for this category
                  const firstProduct = featuredProducts.find(
                    p => (p.category?._id || p.category) === category._id
                  );
                  if (firstProduct) {
                    // For rental products, check rentalImages first, then regular images
                    if (firstProduct.isRental && firstProduct.rentalImages && firstProduct.rentalImages.length > 0) {
                      imageUrl = firstProduct.rentalImages[0];
                    } else if (firstProduct.images && firstProduct.images.length > 0) {
                      imageUrl = firstProduct.images[0];
                    } else {
                      // Use rental-specific demo image for rental category
                      imageUrl = category.name.toLowerCase().includes('rental') ? DEMO_RENTAL_IMAGE : DEMO_CATEGORY_IMAGE;
                    }
                  } else {
                    // Use rental-specific demo image for rental category when no products found
                    imageUrl = category.name.toLowerCase().includes('rental') ? DEMO_RENTAL_IMAGE : DEMO_CATEGORY_IMAGE;
                  }
                } else if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/uploads/')) {
                  imageUrl = `/uploads/${imageUrl.replace(/^\/+/, '')}`;
                }
                
                return (
                  <Link
                    key={category._id}
                    to={`/products?category=${category.name.toLowerCase()}`}
                    className="relative rounded-3xl overflow-hidden shadow-lg group transition-transform duration-300 hover:scale-105 bg-white block"
                  >
                    <div className="card-image-container w-full h-64">
                      <img 
                        src={imageUrl} 
                        alt={category.name} 
                        className="w-full h-full object-cover image-loading"
                        onLoad={(e) => {
                          e.target.classList.remove('image-loading');
                          e.target.classList.add('image-loaded');
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="absolute inset-0 image-fallback"
                        style={{ display: 'none' }}
                      >
                        <div className="text-center">
                          <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-2">
                            <span className="text-pink-600 text-xl font-bold">{category.name.charAt(0)}</span>
                          </div>
                          <p className="text-gray-600 text-sm">{category.name}</p>
                        </div>
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/70 to-transparent p-4">
                      <h3 className="text-white text-lg font-bold">{category.name}</h3>
                      <p className="text-white text-xs">{category.productCount || 0} Products</p>
                    </div>
                  </Link>
                );
              })}
            </div>
            {categoryTotalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-6">
                <button
                  onClick={() => {
                    setCategoryPage(p => Math.max(1, p - 1));
                    window.scrollTo(0, 0);
                  }}
                  disabled={categoryPage === 1}
                  className="px-3 py-1 rounded-full border border-gray-300 text-gray-700 bg-white hover:bg-pink-50 disabled:opacity-50"
                >
                  &lt;
                </button>
                <span className="font-semibold text-gray-700">{categoryPage} / {categoryTotalPages}</span>
                <button
                  onClick={() => {
                    setCategoryPage(p => Math.min(categoryTotalPages, p + 1));
                    window.scrollTo(0, 0);
                  }}
                  disabled={categoryPage === categoryTotalPages}
                  className="px-3 py-1 rounded-full border border-gray-300 text-gray-700 bg-white hover:bg-pink-50 disabled:opacity-50"
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        </section>
      ) : null}

      {/* Featured Products (Latest Arrival) */}
      {productsLoading ? (
        <section className="w-full bg-pink-50 py-16 px-4 md:px-8">
          <div className="w-full px-0">
            <h2 className="text-3xl font-serif font-bold text-center mb-10 text-pink-900">Latest Arrival</h2>
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
            </div>
          </div>
        </section>
      ) : productsError ? (
        <section className="w-full bg-pink-50 py-16 px-4 md:px-8">
          <div className="w-full px-0">
            <h2 className="text-3xl font-serif font-bold text-center mb-10 text-pink-900">Latest Arrival</h2>
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">{productsError}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="bg-pink-600 text-white px-4 py-2 rounded-lg"
              >
                Retry
              </button>
            </div>
          </div>
        </section>
      ) : latestArrivalProducts.length > 0 ? (
        <section className="w-full bg-pink-50 py-4 sm:py-6 md:py-8 px-2 sm:px-4 md:px-8">
          <div className="w-full px-0">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-center mb-4 sm:mb-6 md:mb-8 text-pink-900">Latest Arrival</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
              {paginatedArrival.map(product => {
                const wishlisted = isWishlisted(product._id);
                const inCart = isInCart(product._id);
                const avgRating = Array.isArray(product.reviews) && product.reviews.length > 0
                  ? product.reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / product.reviews.length
                  : 0;
                return (
                  <div
                    key={product._id}
                    className="group relative bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg overflow-hidden hover:shadow-lg sm:hover:shadow-2xl transition-all duration-300 border border-gray-100 cursor-pointer card-clickable flex flex-col"
                    onClick={() => navigate(`/product/${product._id}`)}
                  >
                    <div className="card-image-container w-full h-48 sm:h-64 md:h-80">
                      <img
                        src={product.images?.[0] || product.rentalImages?.[0] || DEMO_PRODUCT_IMAGE}
                        alt={product.name}
                        className="w-full h-full object-cover image-loading group-hover:scale-105"
                        onLoad={(e) => {
                          e.target.classList.remove('image-loading');
                          e.target.classList.add('image-loaded');
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="absolute inset-0 image-fallback"
                        style={{ display: 'none' }}
                      >
                        <div className="text-center">
                          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                            <span className="text-pink-600 text-lg sm:text-2xl font-bold">{product.name.charAt(0)}</span>
                          </div>
                          <p className="text-gray-600 text-xs sm:text-sm font-medium">{product.name}</p>
                        </div>
                      </div>
                      {/* Add to Wishlist Icon (top right) */}
                      <button
                        onClick={e => { e.stopPropagation(); if (!wishlisted) toggleWishlist(product); }}
                        className={`absolute top-2 sm:top-3 right-2 sm:right-3 bg-white rounded-full p-2 sm:p-2.5 shadow-md transition z-10 hover:bg-pink-100 flex items-center justify-center ${wishlisted ? 'cursor-not-allowed' : ''}`}
                        aria-label={wishlisted ? 'Already in wishlist' : 'Add to wishlist'}
                        disabled={wishlisted}
                        title={wishlisted ? 'Already in wishlist' : 'Add to wishlist'}
                      >
                        {wishlisted ? (
                          <HeartSolidIcon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-pink-600" />
                        ) : (
                          <HeartOutlineIcon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-gray-400" />
                        )}
                      </button>
                      {/* Add to Cart Icon (bottom right) */}
                      <button
                        onClick={e => { e.stopPropagation(); if (!inCart) addToCart(product, 1, 'M', 'Default'); }}
                        className={`absolute bottom-2 sm:bottom-3 right-2 sm:right-3 bg-white rounded-full p-2 sm:p-2.5 shadow-md transition z-10 hover:bg-pink-100 flex items-center justify-center ${inCart ? 'cursor-not-allowed' : ''}`}
                        aria-label={inCart ? 'Already in cart' : 'Add to cart'}
                        disabled={inCart}
                        title={inCart ? 'Already in cart' : 'Add to cart'}
                      >
                        {inCart ? (
                          <ShoppingBagSolidIcon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-pink-600" />
                        ) : (
                          <ShoppingBagOutlineIcon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <div className="p-3 sm:p-4 md:p-6 flex flex-col justify-between flex-grow">
                      <div>
                        <h3 className="text-xs sm:text-sm md:text-base lg:text-xl font-bold text-gray-900 mb-2 sm:mb-3 text-left leading-tight line-clamp-2">{product.name}</h3>
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <ProductPrice
                            product={product}
                            saleClassName="text-pink-600 font-bold text-sm sm:text-base md:text-lg lg:text-xl"
                            retailClassName="text-gray-400 line-through text-xs sm:text-sm md:text-base"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 mt-auto">
                        {[...Array(5)].map((_, i) => (
                          <StarIcon key={i} className={`h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 ${i < Math.round(avgRating) ? 'text-yellow-400' : 'text-gray-300'}`} />
                        ))}
                        <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-600 font-semibold">
                          {Array.isArray(product.reviews) && product.reviews.length > 0 ? `${avgRating.toFixed(1)} (${product.reviews.length})` : 'No reviews'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {arrivalTotalPages > 1 && (
              <div className="flex items-center justify-center gap-2 sm:gap-4 mt-2 sm:mt-3">
              <button
                onClick={() => {
                  setArrivalPage(p => Math.max(1, p - 1));
                  window.scrollTo(0, 0);
                }}
                disabled={arrivalPage === 1}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full border border-pink-400 bg-white text-pink-600 text-lg sm:text-2xl font-bold shadow hover:bg-pink-50 hover:text-white hover:bg-pink-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                &#8592;
              </button>
                <span className="font-semibold text-gray-700 text-sm sm:text-base">{arrivalPage} / {arrivalTotalPages}</span>
                <button
                  onClick={() => {
                    setArrivalPage(p => Math.min(arrivalTotalPages, p + 1));
                    window.scrollTo(0, 0);
                  }}
                  disabled={arrivalPage === arrivalTotalPages}
                  className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full border border-pink-400 bg-white text-pink-600 text-lg sm:text-2xl font-bold shadow hover:bg-pink-50 hover:text-white hover:bg-pink-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Next page"
                >
                  &#8594;
                </button>
              </div>
            )}
          </div>
        </section>
      ) : null}
{/* Image Gallery Section */}
{galleryProducts.length > 0 && (
        <div className="w-full px-4 md:px-8 py-12">
          <h2 className="text-3xl font-serif font-bold text-center mb-10 text-pink-900">Image Gallery</h2>
          <div className="flex gap-6 overflow-x-auto pb-4">
            {galleryProducts.map(product => (
              <Link
                key={product._id}
                to={`/product/${product._id}`}
                className="bg-white rounded-2xl shadow-lg p-4 flex flex-col items-center min-w-[180px] max-w-[200px] mx-2 rotate-[-2deg] hover:rotate-1 transition-transform duration-200"
                style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}
              >
                <div className="w-40 h-40 bg-gray-100 rounded-xl overflow-hidden mb-3 flex items-center justify-center">
                  <img src={product.images?.[0] || DEMO_PRODUCT_IMAGE} alt={product.name} className="w-full h-full object-cover" />
                </div>
                <div className="font-bold text-lg text-gray-900 text-center" style={{ fontFamily: 'cursive' }}>{product.name}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Women Collections Section */}
      {womenCollectionProducts.length > 0 && (
        <div className="w-full px-2 sm:px-4 md:px-8 py-4 sm:py-6 md:py-8 bg-gray-100">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-center mb-4 sm:mb-6 md:mb-8 text-pink-900">Women Collections</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
            {paginatedWomen.map(product => (
              <div 
                key={product._id} 
                className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg sm:shadow-xl overflow-hidden border border-pink-100 hover:shadow-xl sm:hover:shadow-2xl transition-all duration-300 group flex flex-col cursor-pointer card-clickable"
                onClick={() => navigate(`/product/${product._id}`)}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={product.images?.[0] || DEMO_PRODUCT_IMAGE} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-3 sm:p-4 md:p-6 text-center flex flex-col justify-center items-center">
                  <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-1 sm:mb-2 text-center leading-tight">{product.name}</h3>
                </div>
              </div>
            ))}
          </div>
          {/* Pagination Controls */}
          {womenTotalPages > 1 && (
            <div className="flex items-center justify-center gap-2 sm:gap-4 mt-4 sm:mt-6">
              <button
                onClick={() => {
                  setWomenPage(p => Math.max(1, p - 1));
                  window.scrollTo(0, 0);
                }}
                disabled={womenPage === 1}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full border border-pink-400 bg-white text-pink-600 text-lg sm:text-2xl font-bold shadow hover:bg-pink-50 hover:text-white hover:bg-pink-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                &#8592;
              </button>
              <span className="font-semibold text-gray-700 text-sm sm:text-base">{womenPage} / {womenTotalPages}</span>
              <button
                onClick={() => {
                  setWomenPage(p => Math.min(womenTotalPages, p + 1));
                  window.scrollTo(0, 0);
                }}
                disabled={womenPage === womenTotalPages}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full border border-pink-400 bg-white text-pink-600 text-lg sm:text-2xl font-bold shadow hover:bg-pink-50 hover:text-white hover:bg-pink-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                &#8594;
              </button>
            </div>
          )}
      </div>
      )}

      {/* Dynamic Tag-Based Product Sections */}
      {uniqueTags.map(tag => {
        const tagProducts = getProductsByTag(tag);
        if (tagProducts.length === 0) return null;

        return (
          <section key={tag} className="w-full bg-gradient-to-br from-pink-50 to-purple-50 py-16 px-4 md:px-8">
            <div className="w-full px-0">
              <h2 className="text-3xl font-serif font-bold text-center mb-10 text-pink-900 capitalize">
                {tag.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10">
                {tagProducts.slice(0, 4).map(product => {
                  const wishlisted = isWishlisted(product._id);
                  const inCart = isInCart(product._id);
                  const avgRating = Array.isArray(product.reviews) && product.reviews.length > 0
                    ? product.reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / product.reviews.length
                    : 0;
                  return (
                    <div
                      key={product._id}
                      className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300 border border-gray-100 cursor-pointer card-clickable"
                      onClick={() => navigate(`/product/${product._id}`)}
                    >
                      <div className="card-image-container w-full h-80">
                        <img
                          src={product.images?.[0] || product.rentalImages?.[0] || DEMO_PRODUCT_IMAGE}
                          alt={product.name}
                          className="w-full h-full object-cover image-loading group-hover:scale-105"
                          onLoad={(e) => {
                            e.target.classList.remove('image-loading');
                            e.target.classList.add('image-loaded');
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div 
                          className="absolute inset-0 image-fallback"
                          style={{ display: 'none' }}
                        >
                          <div className="text-center">
                            <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                              <span className="text-pink-600 text-2xl font-bold">{product.name.charAt(0)}</span>
                            </div>
                            <p className="text-gray-600 text-sm font-medium">{product.name}</p>
                          </div>
                        </div>
                        {/* Add to Wishlist Icon (top right) */}
                        <button
                          onClick={e => { e.stopPropagation(); if (!wishlisted) toggleWishlist(product); }}
                          className={`absolute top-3 right-3 bg-white rounded-full p-2 shadow transition z-10 hover:bg-pink-100 ${wishlisted ? 'cursor-not-allowed' : ''}`}
                          aria-label={wishlisted ? 'Already in wishlist' : 'Add to wishlist'}
                          disabled={wishlisted}
                          title={wishlisted ? 'Already in wishlist' : 'Add to wishlist'}
                        >
                          {wishlisted ? (
                            <HeartSolidIcon className="h-6 w-6 text-pink-600" />
                          ) : (
                            <HeartOutlineIcon className="h-6 w-6 text-gray-400" />
                          )}
                        </button>
                        {/* Add to Cart Icon (bottom right) */}
                        <button
                          onClick={e => { e.stopPropagation(); if (!inCart) addToCart(product, 1, 'M', 'Default'); }}
                          className={`absolute bottom-3 right-3 bg-white rounded-full p-2 shadow transition z-10 hover:bg-pink-100 ${inCart ? 'cursor-not-allowed' : ''}`}
                          aria-label={inCart ? 'Already in cart' : 'Add to cart'}
                          disabled={inCart}
                          title={inCart ? 'Already in cart' : 'Add to cart'}
                        >
                          {inCart ? (
                            <ShoppingBagSolidIcon className="h-6 w-6 text-pink-600" />
                          ) : (
                            <ShoppingBagOutlineIcon className="h-6 w-6 text-gray-400" />
                          )}
                        </button>
                      </div>
                      <div className="p-6 text-center">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <ProductPrice
                            product={product}
                            saleClassName="text-pink-600 font-bold text-lg"
                            retailClassName="text-gray-400 line-through text-base"
                          />
                        </div>
                        <div className="flex items-center justify-center mb-2">
                          {[...Array(5)].map((_, i) => (
                            <StarIcon key={i} className={`h-4 w-4 ${i < Math.round(avgRating) ? 'text-yellow-400' : 'text-gray-300'}`} />
                          ))}
                          <span className="ml-2 text-xs text-gray-600 font-semibold">
                            {Array.isArray(product.reviews) && product.reviews.length > 0 ? `${avgRating.toFixed(1)} (${product.reviews.length})` : 'No reviews'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {tagProducts.length > 4 && (
                <div className="text-center mt-4 sm:mt-6">
                  <Link
                    to={`/products?tag=${tag}`}
                    className="inline-block bg-pink-600 text-white px-8 py-3 rounded-full font-semibold shadow hover:bg-pink-700 transition"
                  >
                    View All {tag.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Products
                  </Link>
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* Top Brands */}
      {/* <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-2xl font-bold mb-6">Top Brands</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {brands.map((brand) => (
            <div
              key={brand.name}
              className="bg-white p-4 rounded-full shadow-md hover:shadow-lg transition flex items-center justify-center w-36 h-36 mx-auto"
            >
              <img
                src={brand.image}
                alt={brand.name}
                className="w-24 h-24 object-contain rounded-full"
              />
            </div>
          ))}
        </div>
      </div> */}

      {/* Events Section */}
      {eventsLoading ? (
        <div className="w-full px-4 md:px-8 py-16">
          <div className="mb-10">
            <h2 className="text-3xl font-serif font-bold text-center mb-10 text-pink-900">Upcoming Events</h2>
        </div>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
          </div>
        </div>
      ) : eventsError ? (
        <div className="w-full px-4 md:px-8 py-16">
          <div className="mb-10">
            <h2 className="text-3xl font-serif font-bold text-center mb-10 text-pink-900">Upcoming Events</h2>
          </div>
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">{eventsError}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-pink-600 text-white px-4 py-2 rounded-lg"
            >
              Retry
            </button>
          </div>
        </div>
      ) : events.length > 0 ? (
        <div className="w-full px-4 md:px-8 py-16">
          <div className="mb-10">
            <h2 className="text-3xl font-serif font-bold text-center mb-10 text-pink-900">Upcoming Events</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {events.map((event) => (
              <div key={event.id} className="bg-gradient-to-br from-pink-50 to-indigo-50 rounded-3xl shadow-xl overflow-hidden border border-pink-100 hover:shadow-2xl transition-all duration-300 group">
                <div className="aspect-[16/9] overflow-hidden rounded-t-3xl">
                  <img
                    src={(event.images && event.images.length > 0 ? event.images[0] : DEMO_EVENT_IMAGE)}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="bg-pink-100 text-pink-600 rounded-full px-3 py-1 text-sm font-semibold flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5" />
                      {new Date(event.date).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{event.title}</h3>
                  <p className="text-gray-600 mb-4">{event.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 font-medium bg-indigo-50 rounded-full px-4 py-1">{event.location}</span>
                    <button className="bg-pink-600 text-white px-6 py-2 rounded-full shadow hover:bg-pink-700 transition-colors font-semibold">
                      Register Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Blog Section */}
      {blogsLoading ? (
        <div className="bg-gradient-to-br from-pink-50 to-indigo-50 py-16 px-4 md:px-8">
          <div className="w-full px-0">
            <div className="mb-10">
              <h2 className="text-3xl font-serif font-bold text-center mb-10 text-pink-900">Latest from Our Blog</h2>
            </div>
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
            </div>
          </div>
        </div>
      ) : blogsError ? (
        <div className="bg-gradient-to-br from-pink-50 to-indigo-50 py-16 px-4 md:px-8">
          <div className="w-full px-0">
            <div className="mb-10">
              <h2 className="text-3xl font-serif font-bold text-center mb-10 text-pink-900">Latest from Our Blog</h2>
            </div>
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">{blogsError}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="bg-pink-600 text-white px-4 py-2 rounded-lg"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      ) : blogs.length > 0 ? (
        <div className="bg-gradient-to-br from-pink-50 to-indigo-50 py-16 px-4 md:px-8">
          <div className="w-full px-0">
            <div className="mb-10">
              <h2 className="text-3xl font-serif font-bold text-center mb-10 text-pink-900">Latest from Our Blog</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
              {blogs.map((blog) => (
                <div key={blog.id} className="relative bg-white rounded-2xl shadow-xl overflow-hidden border border-pink-100 hover:shadow-2xl transition-all duration-300 group">
                  <div className="relative h-48 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/90" />
                    <img
                      src={(blog.images && blog.images.length > 0 ? blog.images[0] : DEMO_BLOG_IMAGE)}
                      alt={blog.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="pt-16 pb-6 px-6 text-center">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <span className="bg-indigo-100 text-indigo-600 rounded-full px-3 py-1 text-sm font-semibold flex items-center gap-2">
                        <UserIcon className="h-5 w-5" />
                        {blog.author}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="bg-pink-100 text-pink-600 rounded-full px-3 py-1 text-sm font-semibold">
                        {new Date(blog.date).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{blog.title}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{blog.excerpt}</p>
                    <Link
                      to={`/blog/${blog.id}`}
                      className="text-pink-600 hover:text-pink-700 font-semibold flex items-center justify-center text-sm"
                    >
                      Read More <ArrowRightIcon className="h-5 w-5 ml-2" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* Customer Reviews Section — Google reviews */}
      <div className="w-full px-4 md:px-8 py-16 bg-white">
        <div className="mb-10 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <GoogleMark className="w-8 h-8" />
            <h2 className="text-3xl font-serif font-bold text-pink-900">What Our Customers Say</h2>
          </div>
          <p className="text-gray-600">Google reviews from theVastra.store customers</p>
          {reviews.length > 0 && (
            <p className="mt-2 text-sm text-gray-500">
              {reviews.length} review{reviews.length === 1 ? '' : 's'}
            </p>
          )}
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 px-4 py-2 rounded-full shadow-sm"
          >
            <GoogleMark className="w-5 h-5" />
            See / write a Google review
          </a>
        </div>

        {reviewsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
          </div>
        ) : reviewsError ? (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">{reviewsError}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-pink-600 text-white px-4 py-2 rounded-lg"
            >
              Retry
            </button>
          </div>
        ) : reviews.length > 0 ? (
          <div className="relative">
            {reviews.length > 5 && (
              <button
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 rounded-full w-12 h-12 flex items-center justify-center shadow hover:bg-pink-100 mx-4"
                onClick={() => setReviewIndex(i => Math.max(0, i - 1))}
                disabled={reviewIndex === 0}
                aria-label="Previous review"
              >
                <span className="text-2xl">&lt;</span>
              </button>
            )}
            <div
              ref={reviewScrollRef}
              className="flex gap-6 overflow-x-auto pb-4 no-scrollbar scroll-smooth"
              style={{ scrollBehavior: 'smooth', minHeight: 320 }}
            >
              {reviews.map((review, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 hover:shadow-2xl transition-all duration-300 min-w-[320px] max-w-[340px] flex-shrink-0"
                  style={{ opacity: Math.abs(index - reviewIndex) > 2 ? 0.3 : 1 }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarIcon
                          key={star}
                          className={`w-5 h-5 ${
                            star <= review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    {review.source === 'google' && <GoogleMark className="w-5 h-5" />}
                  </div>
                  <p className="text-gray-700 mb-4 italic line-clamp-5">"{review.comment}"</p>
                  {review.images && review.images.length > 0 && (
                    <div className="flex gap-2 mb-4 overflow-x-auto">
                      {review.images.map((image, imgIndex) => (
                        <img
                          key={imgIndex}
                          src={image}
                          alt={`Review ${index + 1}`}
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-pink-400 to-indigo-400 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {review.user?.name?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{review.user?.name || 'Anonymous'}</p>
                      <p className="text-sm text-gray-500">
                        Posted {review.source === 'google' ? 'on Google · ' : ''}{new Date(review.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {review.product && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500">Review for:</p>
                      <p className="text-sm font-medium text-gray-900">{review.product.name}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {reviews.length > 5 && (
              <button
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 rounded-full w-12 h-12 flex items-center justify-center shadow hover:bg-pink-100 mx-4"
                onClick={() => setReviewIndex(i => Math.min(reviews.length - 1, i + 1))}
                disabled={reviewIndex === reviews.length - 1}
                aria-label="Next review"
              >
                <span className="text-2xl">&gt;</span>
              </button>
            )}
            <div className="text-center mt-8">
              <Link
                to="/reviews"
                className="inline-flex items-center text-pink-700 font-semibold hover:underline"
              >
                View all reviews
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </Link>
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-500">Be the first to review us on Google.</p>
        )}
      </div>

      {/* Newsletter */}
      <div className="bg-pink-50 py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Subscribe to Our Newsletter</h2>
          <p className="text-gray-600 mb-6">Get the latest updates on new products and upcoming sales</p>
          <div className="max-w-md mx-auto flex">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-l-lg border border-gray-300 focus:outline-none focus:border-pink-500"
            />
            <button className="bg-pink-600 text-white px-6 py-3 rounded-r-lg hover:bg-pink-700 transition">
              Subscribe
            </button>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default Home;